import {
  Button,
  ButtonProps,
  Stack,
  SvgIconProps,
  Typography,
  useTheme,
} from '@mui/material'
import {
  MouseEvent,
  TouchEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Fade, Icons } from 'utoniq-ui'

import { Organism, OrganismFrame } from '@/core/architecture'

type SwipeButtonProps = {
  size?: 'medium' | 'large'
  textOn: string
  textOff: string
  loading?: boolean
  disabled?: boolean
  succeed?: boolean
  fullWidth?: boolean
  onClick: () => Promise<void>
  color?: ButtonProps['color']
  openConfirm?: boolean
  reverse?: boolean
}

export const SwipeButton: Organism<SwipeButtonProps> = ({
  size = 'medium',
  textOn,
  textOff,
  loading,
  disabled,
  succeed,
  onClick,
  fullWidth,
  color = 'primary',
  openConfirm = false,
  reverse = false,
}) => {
  const { palette } = useTheme()
  const container = useRef<HTMLDivElement | null>(null)
  const slider = useRef<HTMLDivElement | null>(null)
  const btnSlider = useRef<HTMLDivElement | null>(null)

  const sliderLeft = useRef<number>(0)
  const containerWidth = useRef<number>(0)
  const startX = useRef<number>(0)
  const isDragging = useRef<boolean>(false)
  const unmounted = useRef<boolean>(false)
  const [unlocked, setUnlocked] = useState<boolean>(false)
  const width = (size === 'large' ? 60 : 44) - 8

  const isTouchDevice = useMemo<boolean>(() => {
    if (typeof window === 'undefined') return false
    return 'ontouchstart' in document.documentElement
  }, [])

  const updateSliderStyle = useCallback(() => {
    if (unmounted.current || unlocked) return
    if (!slider.current) {
      return
    }
    if (!sliderLeft.current) {
      slider.current.style.left = width - 4 + 'px'
    } else {
      slider.current.style.left = sliderLeft.current + width + 'px'
    }
    if (!btnSlider.current) return
    // update bgcolor of btn slide when user scroll about 5px
    if (sliderLeft.current > 5) {
      btnSlider.current.style.backgroundColor = `${palette.grey['200']}`
    } else {
      btnSlider.current.style.backgroundColor = 'white'
    }
  }, [unlocked, palette, reverse])

  const onDrag = useCallback(
    // eslint-disable-next-line
    (e: any) => {
      if (unmounted.current || unlocked) return
      if (isDragging.current) {
        if (isTouchDevice) {
          sliderLeft.current = Math.min(
            Math.max(
              0,
              reverse
                ? startX.current - (e.touches?.[0]?.clientX ?? 0)
                : (e.touches?.[0]?.clientX ?? 0) - startX.current
            ),
            containerWidth.current
          )
        } else {
          sliderLeft.current = Math.min(
            Math.max(
              0,
              reverse ? startX.current - e.clientX : e.clientX - startX.current
            ),
            containerWidth.current
          )
        }
        updateSliderStyle()
      }
    },
    [isTouchDevice, unlocked, updateSliderStyle, reverse]
  )

  const reset = useCallback(() => {
    if (unmounted.current) return

    if (openConfirm) {
      sliderLeft.current = '100%' as unknown as number
      return
    }

    setUnlocked(false)
    sliderLeft.current = 0
    updateSliderStyle()
  }, [updateSliderStyle, openConfirm])

  const onSuccess = useCallback(() => {
    setUnlocked(true)
    if (container.current) {
      container.current.style.width = container.current.clientWidth + 'px'
    }
    onClick().catch(() => reset())
  }, [onClick, reset])

  const stopDrag = useCallback(() => {
    if (unmounted.current || unlocked || !isDragging.current) return
    isDragging.current = false
    if (sliderLeft.current > containerWidth.current * 0.9) {
      sliderLeft.current = containerWidth.current
      onSuccess()
    } else {
      sliderLeft.current = 0
    }
    updateSliderStyle()
  }, [unlocked, updateSliderStyle, onSuccess])

  const startDrag = useCallback(
    (e: MouseEvent<HTMLDivElement> | TouchEvent<HTMLDivElement>) => {
      if (unmounted.current || unlocked) return
      isDragging.current = true
      if (isTouchDevice && 'touches' in e) {
        startX.current = e.touches?.[0]?.clientX ?? 0
      }
      if (!isTouchDevice && 'clientX' in e) {
        startX.current = e.clientX ?? 0
      }
    },
    [isTouchDevice, unlocked]
  )

  useEffect(() => {
    if (isTouchDevice) {
      document.addEventListener('touchmove', onDrag)
      document.addEventListener('touchend', stopDrag)
    } else {
      document.addEventListener('mousemove', onDrag)
      document.addEventListener('mouseup', stopDrag)
    }
    containerWidth.current = (container.current?.clientWidth ?? 0) - (width - 4)

    return () => {
      if (isTouchDevice) {
        document.removeEventListener('touchmove', onDrag)
        document.removeEventListener('touchend', stopDrag)
      } else {
        document.removeEventListener('mousemove', onDrag)
        document.removeEventListener('mouseup', stopDrag)
      }
    }
  }, [isTouchDevice, onDrag, stopDrag])

  useEffect(() => {
    if (succeed !== undefined) {
      setUnlocked(succeed)
      if (!succeed) {
        reset()
      } else {
        sliderLeft.current = containerWidth.current
        updateSliderStyle()
      }
    }
  }, [succeed, reset])

  return (
    <OrganismFrame>
      <Button
        disabled={unlocked || loading || succeed || disabled}
        loading={loading}
        disableRipple
        color={color}
        fullWidth={fullWidth}
        sx={{
          borderRadius: 1,
          width: fullWidth ? undefined : 244,
          height: size === 'large' ? 60 : 44,
          px: 2,
          py: 1,
          overflow: 'hidden',
          '*': {
            userSelect: 'none',
            WebkitTouchCallout: 'none',
          },
        }}
      >
        <Stack
          sx={{
            width: '100%',
            height: '100%',
            position: 'relative',
            bgcolor: 'inherit',
            visibility: loading ? 'hidden' : 'visibility',
          }}
          alignItems="center"
          justifyContent="center"
          ref={container}
        >
          <Stack
            ref={slider}
            onMouseDown={startDrag}
            onTouchStart={startDrag}
            alignItems="center"
            justifyContent="center"
            sx={{
              width: '100%',
              position: 'absolute',
              height: width,
              top: 0,
              left: width - 4,
              ml: '-100%',
              zIndex: 2,
              cursor: 'pointer',
              bgcolor: 'inherit',
              ...(unlocked
                ? {
                    left: '100%',
                    pointerEvents: 'none',
                    cursor: 'default',
                  }
                : {}),
            }}
          >
            {unlocked && (
              <Fade style={{ width: '100%' }} in>
                <Typography
                  variant={size === 'large' ? 'h5' : 'subtitle1'}
                  align="center"
                  width="100%"
                  color="text.hint"
                >
                  {textOff}
                </Typography>
              </Fade>
            )}
            <Stack
              ref={btnSlider}
              alignItems="center"
              justifyContent="center"
              sx={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: width,
                height: width,
                bgcolor: unlocked || disabled ? 'grey.200' : 'white',
                borderRadius: 1,
              }}
            >
              {unlocked ? (
                <Fade style={{ height: 20 }} in>
                  <Icons.Check
                    sx={{
                      color: 'grey.400',
                      width: size === 'large' ? 28 : 20,
                      height: size === 'large' ? 28 : 20,
                      mt: size === 'large' ? -1 : 0,
                    }}
                  />
                </Fade>
              ) : (
                <Fade style={{ height: 20 }} in>
                  <Icons.Transition
                    color={color as SvgIconProps['color']}
                    sx={{
                      color: disabled ? 'grey.400' : undefined,
                      width: size === 'large' ? 28 : 20,
                      height: size === 'large' ? 28 : 20,
                      mt: size === 'large' ? -1 : 0,
                    }}
                  />
                </Fade>
              )}
            </Stack>
          </Stack>
          {!unlocked && (
            <Fade style={{ width: '100%' }} in>
              <Typography
                variant={size === 'large' ? 'h5' : 'subtitle1'}
                align="center"
                width="100%"
                color="white"
              >
                {textOn}
              </Typography>
            </Fade>
          )}
        </Stack>
      </Button>
    </OrganismFrame>
  )
}
